import { cleanAIFormatting } from '../lib/contentCleaner.js';

import { supabase } from '../supabase';

export const fetchSocialMediaPosts = async () => {
    const { data, error } = await supabase
        .from('social_media_posts')
        .select('*')
        .order('created_at', { ascending: true });

    if (error) {
        throw new Error('Failed to fetch posts');
    }

    return data;
};

export const updateSocialMediaPost = async (updatedPost) => {
    const { data, error } = await supabase
        .from('social_media_posts')
        .update({ title: updatedPost.title, content: updatedPost.content })
        .eq('id', updatedPost.id)
        .select();

    if (error) {
        throw new Error('Failed to update post');
    }

    return data[0];
};

export const createSocialMediaPost = async (newPost) => {
    const { data, error } = await supabase
        .from('social_media_posts')
        .insert([newPost])
        .select();

    if (error) {
        throw new Error('Failed to create post');
    }

    return data[0];
};

/**
 * Generates social media content using AI (or simulation).
 * @param {string} platform - The target platform (e.g., 'instagram')
 * @param {string} contentType - The type of content (e.g., 'post', 'story')
 * @param {Object} brainData - The brand intelligence data
 * @returns {Promise<Object>} - The generated content object
 */
export const generateSocialContent = async (platform, contentType, brainData, exampleUrl = null, options = {}) => {
    const brandVoice = brainData.brand?.brandVoice || 'Professional';
    const productName = options.productName || brainData.products?.[0]?.name || brainData.brand?.brandName || brainData.brand?.name || 'SEVEN HATS®';
    const audience = brainData.audience?.primary || 'Customers';

    // Extract deeper context for better generation relevance
    const description = brainData.brand?.description || brainData.brand?.mission || brainData.brand?.missionStatement || '';

    // Prioritize saved strategy keywords if available
    const keywords = Array.isArray(brainData.strategy?.keywords) && brainData.strategy.keywords.length > 0
        ? brainData.strategy.keywords.join(', ')
        : (brainData.brand?.keywords || brainData.brand?.values || '');

    const workspaceId = options.workspaceId || brainData.brand?.workspace_id;

    try {
        const { data, error } = await supabase.functions.invoke('generate-social-content', {
            body: {
                platform,
                contentType,
                brandVoice,
                productName,
                audience,
                description,
                keywords,
                exampleUrl,
                step: options.step || 'full',
                content: options.content || null,
                style: options.style || 'modern',
                workspaceId,
                topic: options.topic || null,
                workspaceName: options.workspaceName,
                duration: options.duration || '15',
                format: options.format || '1:1'
            }
        });

        // Handle invocation errors (network, auth, etc.)
        if (error) {
             console.error("Supabase Function Error:", error);

             // Extract specific error message from the response body if available
             if (error.context && typeof error.context.json === 'function') {
                 try {
                     const errorBody = await error.context.json();
                     if (errorBody && errorBody.error) {
                         // Re-throw as a standard Error with the specific message
                         const specificError = new Error(errorBody.error);
                         specificError.name = 'BackendError';
                         throw specificError;
                     } else {
                         console.warn("Error body did not contain 'error' field:", errorBody);
                     }
                 } catch (e) {
                     // If we just threw the specific error, re-throw it up
                     if (e.name === 'BackendError') {
                         throw e;
                     }
                     console.warn("Failed to parse error body JSON:", e);
                 }
             }

             if (error.message && error.message.includes('429')) throw new Error("Rate limit exceeded. Please try again later.");
             throw error;
        }

        // Handle application errors returned by the function
        if (data && data.error) {
            // Check for specific error signatures
            if (data.error.includes('OpenAI API Key is not configured')) {
                throw new Error("Configuration Error: OpenAI API Key is missing. Please check the Troubleshooting guide.");
            }
            if (data.error.includes('Rate limit exceeded')) {
                throw new Error("AI Usage Limit Exceeded. Please try again later.");
            }
            throw new Error(data.error);
        }


        // Ensure social posts are clean from Markdown
        if (data && data.caption && platform) {
            data.caption = cleanAIFormatting(data.caption, platform);
        }
        return data;
    } catch (err) {
        console.warn("AI Generation failed:", err);

        // Critical: Do NOT swallow configuration or rate limit errors.
        // Re-throw them so the UI can inform the user.
        if (err.message.includes('Configuration Error') ||
            err.message.includes('Limit Exceeded') ||
            err.message.includes('Rate limit') ||
            err.message.includes('API Key')) {
            throw err;
        }

        // Fallback only for generic failures (e.g., timeouts, unknown 500s) if preferred,
        // OR re-throw everything to ensure the user knows something is wrong.
        // Given the user is troubleshooting, showing the error is better than a fake success.
        throw err;
    }
};

/**
 * Saves a list of posts to the database.
 * @param {Array} posts - Array of post objects
 * @returns {Promise<void>}
 */
export const saveSocialPosts = async (posts) => {
    const { error } = await supabase.from('social_media_posts').insert(posts);
    if (error) throw error;
};

/**
 * Publishes a post to a social media platform via Edge Function.
 * @param {Object} post - The post object to publish
 * @returns {Promise<boolean>} - True if successful
 */
export const publishSocialPost = async (post) => {
    console.log(`Publishing to ${post.platform}...`, post);

    // Call the Edge Function to handle real publishing
    const { data, error } = await supabase.functions.invoke('publish-social-post', {
        body: {
            post: {
                ...post,
                content: post.content,
                link: post.link || null, // Ensure link is passed if available
                image: post.image || null
            },
            platform: post.platform,
            workspaceId: post.workspace_id
        }
    });

    if (error) {
        console.error("Publishing Edge Function Error:", error);

        let specificMessage = null;
        if (error.context && typeof error.context.json === 'function') {
            try {
                const errorBody = await error.context.json();
                if (errorBody && errorBody.error) {
                    specificMessage = errorBody.error;
                }
            } catch (e) {
                // ignore parsing errors
            }
        }

        if (specificMessage) {
            throw new Error(specificMessage);
        }

        throw new Error(error.message || "Failed to contact publishing service");
    }

    if (data?.error) {
        throw new Error(data.error);
    }

    // Save platformId (external_id) to database if post exists
    if (post.id && data?.platformId) {
        const { error: updateError } = await supabase.from('social_media_posts')
            .update({
                external_id: data.platformId,
                status: 'Published',
                published_at: new Date().toISOString()
            })
            .eq('id', post.id);

        if (updateError) {
            console.warn("Failed to update post with external ID:", updateError);
            // Non-critical error, so we don't throw, but useful for debugging
        }
    }

    return true;
};

/**
 * Runs the Auto-Scheduler to generate weekly posts.
 * @param {string} workspaceId - The active workspace ID
 * @param {Object} brainData - The brand intelligence data
 * @param {Object} settings - Auto-scheduler settings { enabled, platforms, mix }
 * @param {string} ownerId - The workspace owner ID for notifications
 * @returns {Promise<number>} - Number of posts created
 */
export const runAutoScheduler = async (workspaceId, brainData, settings, ownerId = null) => {
    if (!settings.enabled || !settings.platforms || settings.platforms.length === 0) {
        throw new Error("Auto-scheduler is disabled or no platforms selected.");
    }

    const postsToCreate = [];
    const daysToSchedule = 7;
    const platforms = settings.platforms;
    const contentTypes = ['post', 'carousel']; // Removed video formats to prevent Veo generation timeouts

    // Generate 7 posts (one for each day starting tomorrow)
    for (let i = 1; i <= daysToSchedule; i++) {
        // Randomly pick platform and type
        const platform = platforms[Math.floor(Math.random() * platforms.length)];
        const contentType = contentTypes[Math.floor(Math.random() * contentTypes.length)];

        // Generate Content
        // We await sequentially to avoid rate limits on the mock/real AI
        const content = await generateSocialContent(platform, contentType, brainData, null, { workspaceId, step: 'full' });

        // Calculate date
        const date = new Date();
        date.setDate(date.getDate() + i);
        date.setHours(10, 0, 0, 0); // Default to 10 AM

        postsToCreate.push({
            workspace_id: workspaceId,
            title: `Auto-Draft: ${platform} ${contentType}`,
            content: content.caption || "Content generation failed",
            status: 'Pending Approval', // Saved as Pending Approval for explicit review
            platform: platform,
            author: 'Auto-Pilot',
            avatar: '/logo.webp',
            scheduled_at: date.toISOString(),
            image: content.imageUrl || null,
            media_type: content.mediaType || (content.imageUrl?.includes('mp4') || content.imageUrl?.startsWith('galaxy_run_') ? 'video' : 'image'),
        });
    }

    await saveSocialPosts(postsToCreate);

    // Create Notification
    // We try to find the user_id from parameters or brainData
    const userId = ownerId || brainData.brand?.owner_id || null;

    // Only attempt notification if we have a table mock/real
    try {
         await supabase.from('notifications').insert([{
            workspace_id: workspaceId,
            user_id: userId,
            title: 'Auto-Pilot Run Complete',
            message: `Generated ${postsToCreate.length} new drafts for your review.`,
            type: 'success',
            link: '/engagement?tab=posts',
            is_read: false,
            gnome_id: '3'
        }]);
    } catch (e) {
        console.warn("Failed to create notification", e);
    }

    return postsToCreate.length;
};

export const generateSocialBatch = async (workspaceId, seedType, seedValue, brainData) => {
    try {
        // Step 1: Get the optimal mix plan from the batch planner edge function
        const { data: planData, error: planError } = await supabase.functions.invoke('generate-social-batch', {
            body: {
                workspaceId,
                seedType,
                seedValue
            }
        });

        if (planError) {
             console.error("Supabase Function Error:", planError);
             if (planError.context && typeof planError.context.json === 'function') {
                 try {
                     const errorBody = await planError.context.json();
                     if (errorBody && errorBody.error) {
                         const specificError = new Error(errorBody.error);
                         specificError.name = 'BackendError';
                         throw specificError;
                     }
                 } catch (e) {
                     if (e.name === 'BackendError') throw e;
                 }
             }
             throw planError;
        }

        if (planData && planData.error) {
            throw new Error(planData.error);
        }

        const optimalMix = planData.optimalMix || [];
        if (optimalMix.length === 0) {
             throw new Error("No valid strategy could be determined from the input.");
        }

        // Step 2: Execute generations sequentially using the robust frontend generateSocialContent helper
        const postsToCreate = [];
        let successCount = 0;

        for (let i = 0; i < optimalMix.length; i++) {
            const postPlan = optimalMix[i];
            try {
                const topic = seedType === 'topic' || seedType === 'trending' ? `${seedValue}. Specific angle: ${postPlan.specificTopic}` : postPlan.specificTopic;
                let exampleUrl = null;
                if (seedType === 'image') {
                    exampleUrl = seedValue;
                } else if (seedType === 'topic' && seedValue && seedValue.startsWith('http')) {
                    // Extract URL if the topic is actually a URL
                    exampleUrl = seedValue;
                }

                const content = await generateSocialContent(
                    postPlan.platform,
                    postPlan.contentType,
                    brainData,
                    exampleUrl,
                    { workspaceId, step: 'full', topic }
                );

                const date = new Date();
                date.setDate(date.getDate() + 1 + i); // Stagger by day
                date.setHours(10, 0, 0, 0);

                postsToCreate.push({
                    workspace_id: workspaceId,
                    title: `Auto-Batch: ${postPlan.platform} ${postPlan.contentType}`,
                    content: content.caption || "Content generation failed",
                    status: 'Pending Approval',
                    platform: postPlan.platform,
                    author: 'AI Strategist',
                    avatar: '/logo.webp',
                    scheduled_at: date.toISOString(),
                    image: content.imageUrl || null,
                    media_type: content.mediaType || (content.imageUrl?.includes('mp4') || content.imageUrl?.startsWith('galaxy_run_') ? 'video' : 'image'),
                });
                successCount++;
            } catch (postErr) {
                 console.error(`Error generating batch post for ${postPlan.platform}:`, postErr);
            }
        }

        if (postsToCreate.length > 0) {
             await saveSocialPosts(postsToCreate);
        }

        return { success: true, count: successCount };

    } catch (err) {
        console.warn("AI Batch Generation failed:", err);
        throw err;
    }
};
